import { Heart, Mic2, MoreHorizontal } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'

import type { LibraryStatus } from '@/api/types'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  ProgressBar,
} from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatMs } from '@/lib/format'

import { Cover } from './Cover'
import { songActions } from './songActions'
import { PlayingIndicator } from './PlayingIndicator'

export interface TrackRowMenuItem {
  label: string
  /** The icon component, e.g. `icon={Trash2}` — not an element. */
  icon?: ComponentType<{ className?: string }>
  onSelect: () => void
  destructive?: boolean
  context?: boolean
  disabled?: boolean
}

export interface TrackRowProps {
  title: string
  artistName: string
  artistHref?: string
  includeArtistMenu?: boolean
  albumName?: string | null
  durationMs?: number | null
  coverUrl?: string | null
  isActive?: boolean
  isPlaying?: boolean
  status?: LibraryStatus
  /** 0-100. Drives the hairline bar along the bottom of the row. */
  progress?: number
  isFavorite?: boolean
  onPlay: () => void
  onToggleFavorite?: () => void
  menuItems?: TrackRowMenuItem[]
  onMenuCloseAutoFocus?: (event: Event) => void
  /** Extra control on the right, e.g. a drag handle or an "Add" button. */
  trailing?: ReactNode
  className?: string
}

function statusNote(status: LibraryStatus | undefined, progress: number | undefined) {
  switch (status) {
    case 'pending':
      return { text: 'Queued', tone: 'text-text-muted' }
    case 'downloading':
      return {
        text:
          typeof progress === 'number' && progress > 0
            ? `Downloading ${Math.round(progress)}%`
            : 'Downloading',
        tone: 'text-accent',
      }
    case 'failed':
      return { text: 'Download failed', tone: 'text-danger' }
    case 'missing':
      return { text: 'File missing', tone: 'text-danger' }
    default:
      return null
  }
}

export function TrackRow({
  title,
  artistName,
  artistHref,
  includeArtistMenu = true,
  albumName,
  durationMs,
  coverUrl,
  isActive = false,
  isPlaying = false,
  status,
  progress,
  isFavorite,
  onPlay,
  onToggleFavorite,
  menuItems,
  onMenuCloseAutoFocus,
  trailing,
  className,
}: TrackRowProps) {
  const navigate = useNavigate()
  const note = statusNote(status, progress)
  const inFlight = status === 'pending' || status === 'downloading'
  const items = menuItems ?? []
  const contextStart = items.findIndex((item) => item.destructive || item.context)
  const split = contextStart < 0 ? items.length : contextStart
  const allMenuItems = [
    ...items.slice(0, split),
    ...songActions({ toggleFavorite: onToggleFavorite, favorite: isFavorite }),
    ...(artistHref && includeArtistMenu ? [{ label: 'Go to artist', icon: Mic2, onSelect: () => void navigate(artistHref) }] : []),
    ...items.slice(split),
  ]

  return (
    <div
      className={cn(
        'group relative flex h-16 items-center overflow-hidden rounded-lg transition-colors',
        isActive ? 'bg-accent-soft' : 'hover:bg-surface-hover',
        className,
      )}
    >
      {/* The play target fills the row; the trailing controls are siblings, never
          nested buttons. */}
      <div
        className="relative flex h-full min-w-0 flex-1 items-center gap-3 rounded-lg px-2 text-left"
      >
        <button
          type="button"
          onClick={onPlay}
          aria-current={isActive ? 'true' : undefined}
          aria-label={`Play ${title} by ${artistName}`}
          className="absolute inset-0 rounded-lg"
        />
        <span className="pointer-events-none relative block shrink-0">
          <Cover src={coverUrl} size="sm" />
          {isActive ? (
            <span className="absolute inset-0 flex items-center justify-center rounded-md bg-overlay">
              <PlayingIndicator animated={isPlaying} />
            </span>
          ) : null}
        </span>

        <span className="pointer-events-none relative flex min-w-0 flex-1 flex-col">
          <span
            className={cn(
              'truncate text-sm font-medium',
              isActive ? 'text-accent' : 'text-text',
            )}
          >
            {title}
          </span>
          <span className="truncate text-sm text-text-muted">
            {note ? <span className={note.tone}>{note.text} · </span> : null}
            {artistHref ? (
              <Link to={artistHref} className="pointer-events-auto rounded-sm hover:text-text hover:underline" onClick={(event) => event.stopPropagation()}>
                {artistName}
              </Link>
            ) : artistName}
            {albumName ? ` · ${albumName}` : ''}
          </span>
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-0.5 pr-1">
        {typeof durationMs === 'number' && durationMs > 0 ? (
          <span className="hidden px-1 text-sm tabular-nums text-text-subtle sm:inline">
            {formatMs(durationMs)}
          </span>
        ) : null}

        {trailing}

        {onToggleFavorite ? (
          <IconButton
            aria-label={isFavorite ? `Remove ${title} from favourites` : `Add ${title} to favourites`}
            aria-pressed={isFavorite ?? false}
            size="sm"
            onClick={onToggleFavorite}
            className={cn('hidden sm:inline-flex', isFavorite && 'text-accent hover:text-accent')}
          >
            <Heart className={cn('h-4 w-4', isFavorite && 'fill-current')} aria-hidden="true" />
          </IconButton>
        ) : null}

        {allMenuItems && allMenuItems.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton aria-label={`More options for ${title}`} size="sm">
                <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent onCloseAutoFocus={onMenuCloseAutoFocus}>
              {allMenuItems.map((item: TrackRowMenuItem) => (
                <DropdownMenuItem
                  key={item.label}
                  icon={item.icon}
                  destructive={item.destructive}
                  disabled={item.disabled}
                  onSelect={item.onSelect}
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      {inFlight ? (
        <ProgressBar
          value={progress ?? 0}
          indeterminate={status === 'pending' || progress === undefined}
          label={`Downloading ${title}`}
          className="absolute inset-x-0 bottom-0 rounded-none"
        />
      ) : null}
    </div>
  )
}
